import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { config } from '@/config';
import { tokenStore } from '@/data/storage/token-store';

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = tokenStore.get();
    if (token) request.headers.set('Authorization', `Bearer ${token}`);
    return request;
  },
  async onResponse({ response }) {
    if (response.status === 401) tokenStore.clear();
    return response;
  },
};

const timeoutFetch: typeof fetch = (input, init) => {
  const ctrl = new AbortController();
  const timer = setTimeout(() => {
    ctrl.abort(new DOMException('Request timed out. Please try again.', 'TimeoutError'));
  }, config.network.requestTimeoutMs);
  const signal = init?.signal
    ? mergeSignals(ctrl.signal, init.signal as AbortSignal)
    : ctrl.signal;
  return fetch(input, { ...init, signal })
    .catch((err) => {
      if (ctrl.signal.aborted) throw ctrl.signal.reason ?? err;
      throw err;
    })
    .finally(() => clearTimeout(timer));
};

function mergeSignals(a: AbortSignal, b: AbortSignal): AbortSignal {
  const ctrl = new AbortController();
  const onAbort = (signal: AbortSignal) => {
    if (!ctrl.signal.aborted) ctrl.abort(signal.reason);
  };
  a.addEventListener('abort', () => onAbort(a));
  b.addEventListener('abort', () => onAbort(b));
  if (a.aborted) onAbort(a);
  if (b.aborted) onAbort(b);
  return ctrl.signal;
}

export const api = createClient<paths>({
  baseUrl: config.apiBaseUrl,
  fetch: timeoutFetch,
});

api.use(authMiddleware);

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

type ApiResult<T> = { data?: T; error?: unknown; response: Response };

export async function unwrap<T>(p: Promise<ApiResult<T>>): Promise<T> {
  const r = await p;
  if (r.error || !r.response.ok) {
    const msg = extractErrorMessage(r.error) ?? `Request failed (${r.response.status})`;
    throw new ApiError(r.response.status, r.error, msg);
  }
  return r.data as T;
}

function extractErrorMessage(e: unknown): string | null {
  if (!e || typeof e !== 'object') return null;
  const obj = e as Record<string, unknown>;
  if (typeof obj.error === 'string') return obj.error;
  if (typeof obj.title === 'string') return obj.title;
  if (typeof obj.message === 'string') return obj.message;
  return null;
}
